import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type AdminAnalytics as AdminData } from "../lib/api";
import { fmtNum } from "../lib/format";

/**
 * Hidden admin dashboard at /admin. Renders raw counts + per-day DAU,
 * top pages and top features over a configurable window. No sidebar
 * entry — reachable by typing the URL — and behind the same shared
 * password as the rest of the app.
 *
 * The queries only fire while this page is mounted, so the analytics
 * tables are never touched during normal operator use.
 */

const WINDOWS: Array<{ label: string; days: number }> = [
  { label: "24 h", days: 1 },
  { label: "7 d", days: 7 },
  { label: "30 d", days: 30 },
  { label: "90 d", days: 90 },
  { label: "1 y", days: 365 },
];

export function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const q = useQuery({
    queryKey: ["admin-analytics", days],
    queryFn: () => api.adminAnalytics(days),
    refetchInterval: 60_000,
  });

  return (
    <div className="min-h-screen bg-bg text-text">
      <div className="max-w-[1100px] mx-auto px-8 py-8 max-md:px-4 flex flex-col gap-6">
        <header className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-dim">
              Admin
            </div>
            <h1 className="text-[18px] font-semibold mt-1">Usage analytics</h1>
            <p className="text-[11.5px] text-muted mt-1 leading-snug max-w-[60ch]">
              Counts come from anonymous browser identifiers stored in
              each visitor's localStorage. &ldquo;Distinct users&rdquo;
              therefore counts distinct browsers, not individual
              accounts — the single shared password makes a true user
              count impossible without per-user logins.
            </p>
          </div>
          <a
            href="/"
            className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded border border-border1 text-muted hover:text-text"
          >
            ← Back to portal
          </a>
        </header>

        <div className="inline-flex bg-surface2 border border-border1 rounded overflow-hidden self-start">
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              type="button"
              onClick={() => setDays(w.days)}
              className={
                "px-3 py-1.5 text-[11px] uppercase tracking-wider " +
                (days === w.days
                  ? "bg-wb text-white"
                  : "text-muted hover:text-text")
              }
            >
              {w.label}
            </button>
          ))}
        </div>

        {q.isLoading && (
          <div className="text-[12px] text-muted">Loading…</div>
        )}
        {q.error && (
          <div className="text-[12px] text-wb">
            {(q.error as Error).message}
          </div>
        )}

        {q.data && <Body data={q.data} />}
      </div>
    </div>
  );
}

function Body({ data }: { data: AdminData }) {
  const t = data.totals;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile label="Distinct users" value={t.distinctUsers} />
        <StatTile label="Distinct IPs" value={t.distinctIps} />
        <StatTile label="Page views" value={t.pageViews} />
        <StatTile label="Logins" value={t.logins} />
        <StatTile label="Events total" value={t.events} />
      </div>

      <section className="card p-5">
        <SectionTitle title="Daily active users" />
        <Sparkbars
          rows={data.dailyActiveUsers}
          accessor={(r) => r.users}
          xLabel={(r) => r.day.slice(5, 10)}
          yLabel={(r) => `${r.users} user${r.users === 1 ? "" : "s"}`}
        />
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RankedTable
          title="Top pages"
          rows={data.topPages.map((p) => ({
            label: p.page,
            primary: p.views,
            secondary: null,
          }))}
          primaryLabel="views"
        />
        <RankedTable
          title="Top features"
          rows={data.topFeatures.map((f) => ({
            label: f.name,
            primary: f.uses,
            secondary: f.users,
          }))}
          primaryLabel="uses"
          secondaryLabel="users"
        />
      </section>

      <section className="card p-5">
        <SectionTitle title="Approximate locations" />
        <p className="text-[11px] text-muted mb-3 leading-snug">
          Resolved from the request IP via ip-api.com (city-level
          accuracy at best — VPNs, mobile data and CDNs all shift the
          result). One row per distinct city.
        </p>
        {data.locations.length === 0 && data.locationsUnknown.events === 0 ? (
          <div className="text-[11.5px] text-muted italic">
            No locations in this window.
          </div>
        ) : (
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-dim text-left">
                <th className="font-medium pb-2">Location</th>
                <th className="font-medium pb-2 text-right">Events</th>
                <th className="font-medium pb-2 text-right">Users</th>
                <th className="font-medium pb-2 text-right">IPs</th>
              </tr>
            </thead>
            <tbody>
              {data.locations.map((l, i) => (
                <tr key={i} className="border-t border-border1">
                  <td className="py-1.5">
                    {l.city}
                    <span className="text-muted">
                      {l.region ? `, ${l.region}` : ""} — {l.country}
                    </span>
                  </td>
                  <td className="py-1.5 text-right mono-nums">
                    {fmtNum(l.events, 0)}
                  </td>
                  <td className="py-1.5 text-right mono-nums text-muted">
                    {fmtNum(l.users, 0)}
                  </td>
                  <td className="py-1.5 text-right mono-nums text-muted">
                    {fmtNum(l.ips, 0)}
                  </td>
                </tr>
              ))}
              {data.locationsUnknown.events > 0 && (
                <tr className="border-t border-border1">
                  <td className="py-1.5 text-muted italic">
                    Unknown / private IP
                  </td>
                  <td className="py-1.5 text-right mono-nums">
                    {fmtNum(data.locationsUnknown.events, 0)}
                  </td>
                  <td className="py-1.5 text-right mono-nums text-muted">—</td>
                  <td className="py-1.5 text-right mono-nums text-muted">
                    {fmtNum(data.locationsUnknown.ips, 0)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="card p-4">
      <div className="text-[10.5px] uppercase tracking-wider text-dim">
        {label}
      </div>
      <div className="text-[22px] font-semibold mt-1 mono-nums">
        {fmtNum(value, 0)}
      </div>
    </div>
  );
}

function SectionTitle({ title }: { title: string }) {
  return (
    <h2 className="text-[13px] font-semibold mb-3">{title}</h2>
  );
}

/**
 * A simple per-day bar chart that stays on the page even when one day
 * dwarfs the rest (max-normalised, min bar width visible). Avoids
 * dragging in a real charting library for a tiny admin-only page.
 */
function Sparkbars<T>({
  rows,
  accessor,
  xLabel,
  yLabel,
}: {
  rows: T[];
  accessor: (r: T) => number;
  xLabel: (r: T) => string;
  yLabel: (r: T) => string;
}) {
  if (rows.length === 0) {
    return <div className="text-[11.5px] text-muted italic">No data in this window.</div>;
  }
  const max = Math.max(1, ...rows.map(accessor));
  return (
    <div className="flex items-end gap-[2px] h-[140px]">
      {rows.map((r, i) => {
        const h = Math.max(2, Math.round((accessor(r) / max) * 130));
        return (
          <div
            key={i}
            className="flex-1 flex flex-col items-center justify-end group relative"
          >
            <div
              className="w-full bg-wb/60 group-hover:bg-wb rounded-sm"
              style={{ height: h }}
              title={`${xLabel(r)} — ${yLabel(r)}`}
            />
            {i % Math.ceil(rows.length / 8) === 0 && (
              <div className="text-[9px] text-dim mt-1 mono-nums">
                {xLabel(r)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface RankedRow {
  label: string;
  primary: number;
  secondary: number | null;
}

function RankedTable({
  title,
  rows,
  primaryLabel,
  secondaryLabel,
}: {
  title: string;
  rows: RankedRow[];
  primaryLabel: string;
  secondaryLabel?: string;
}) {
  return (
    <div className="card p-5">
      <SectionTitle title={title} />
      {rows.length === 0 ? (
        <div className="text-[11.5px] text-muted italic">No data in this window.</div>
      ) : (
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-dim text-left">
              <th className="font-medium pb-2">Name</th>
              <th className="font-medium pb-2 text-right">{primaryLabel}</th>
              {secondaryLabel && (
                <th className="font-medium pb-2 text-right">
                  {secondaryLabel}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border1">
                <td className="py-1.5 mono-nums">{r.label}</td>
                <td className="py-1.5 text-right mono-nums">
                  {fmtNum(r.primary, 0)}
                </td>
                {secondaryLabel && (
                  <td className="py-1.5 text-right mono-nums text-muted">
                    {r.secondary == null ? "—" : fmtNum(r.secondary, 0)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
